import React, { useState, useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './i18n'; // 初始化 i18n
import AdminDashboard from './pages/admin/DashboardPage';
import AdminServices from './pages/admin/ServicesPage';
import AdminBookings from './pages/admin/BookingsPage';
import AdminCalendar from './pages/admin/CalendarPage';
import AdminCustomers from './pages/admin/CustomersPage';
import AdminStaff from './pages/admin/StaffPage';
import AdminSettings from './pages/admin/SettingsPage';
import ProfilePage from './pages/admin/ProfilePage';
import AdminPage from './pages/admin/AdminPage';
import DataIOPage from './pages/admin/DataIOPage';
import ManualPage from './pages/admin/ManualPage';
import BookingLinkPage from './pages/admin/BookingLinkPage';
import StoreHomePage from './pages/booking/StoreHomePage';
import DateTimePage from './pages/booking/DateTimePage';
import StaffSelectionPage from './pages/booking/StaffSelectionPage';
import InfoPage from './pages/booking/InfoPage';
import ConfirmationPage from './pages/booking/ConfirmationPage';
import MyBookingsPage from './pages/booking/MyBookingsPage';
import LoginPage from './pages/booking/LoginPage';
import RegisterPage from './pages/booking/RegisterPage';
import ForgotPasswordPage from './pages/booking/ForgotPasswordPage';
import ResetPasswordPage from './pages/booking/ResetPasswordPage';
import AccountPage from './pages/booking/AccountPage';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import { UserProvider, UserContext } from './context/UserContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { FAQPage, TermsOfServicePage, PrivacyPolicyPage } from '@redpanda/ui';

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useContext(UserContext)!;
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();

  // 判斷是否為客戶預訂頁面
  const isCustomerBookingRoute = location.pathname.startsWith('/book/');

  // 記錄最後活動頁面（排除手冊頁面本身）
  useEffect(() => {
    if (location.pathname !== '/admin/manual') {
      sessionStorage.setItem('lastActivePage', location.pathname);
    }
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="app-loading bg-white dark:bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="text-center">
          {/* 品牌 LOGO */}
          <img
            src="/logo.png"
            alt="RedPanda Tech"
            className="w-48 h-48 mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden text-8xl mb-6">🐼</div>

          {/* 載入動畫 */}
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex">
      {/* 手機版漢堡按鈕 - 不在客戶預訂頁面顯示 */}
      {isAuthenticated && !isCustomerBookingRoute && (
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="md:hidden fixed top-4 left-4 z-30 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        </button>
      )}

      {/* Sidebar - 不在客戶預訂頁面顯示 */}
      {isAuthenticated && !isCustomerBookingRoute && (
        <Sidebar
          className="w-48 h-screen fixed left-0 top-0 z-50"
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* 主要內容區域 - 客戶預訂頁面不需要左邊距 */}
      <div className={`flex-1 flex flex-col ${isAuthenticated && !isCustomerBookingRoute ? 'md:ml-48' : ''}`}>
        {/* 主要內容 */}
        <main className="flex-grow">
          <Routes>
            <Route element={<PrivateRoute />}>
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/calendar" element={<AdminCalendar />} />
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route path="/admin/services" element={<AdminServices />} />
              <Route path="/admin/staff" element={<AdminStaff />} />
              <Route path="/admin/booking-link" element={<BookingLinkPage />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/profile" element={<ProfilePage />} />
              <Route path="/admin/admin" element={<AdminPage />} />
              <Route path="/admin/data-io" element={<DataIOPage />} />
              <Route path="/admin/manual" element={<ManualPage />} />
            </Route>
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            {/* Customer Booking Routes - No auth required */}
            <Route path="/book/:storeId" element={<StoreHomePage />} />
            <Route path="/book/:storeId/login" element={<LoginPage />} />
            <Route path="/book/:storeId/register" element={<RegisterPage />} />
            <Route path="/book/:storeId/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/book/:storeId/reset-password" element={<ResetPasswordPage />} />
            <Route path="/book/:storeId/account" element={<AccountPage />} />
            <Route path="/book/:storeId/datetime" element={<DateTimePage />} />
            <Route path="/book/:storeId/staff" element={<StaffSelectionPage />} />
            <Route path="/book/:storeId/info" element={<InfoPage />} />
            <Route path="/book/:storeId/confirmation" element={<ConfirmationPage />} />
            <Route path="/book/:storeId/my-bookings/:token" element={<MyBookingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* 底部 - 不在客戶預訂頁面顯示 */}
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
};

const App: React.FC = () => (
  <Router
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <ColorThemeProvider>
      <UserProvider>
        <CustomerAuthProvider>
          <AppContent />
        </CustomerAuthProvider>
      </UserProvider>
    </ColorThemeProvider>
  </Router>
);

export default App;
